import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/shadcn/components/ui/dialog';
import { Button } from 'src/shadcn/components/ui/button';
import { useHotToast } from 'src/utils/useHotToast';
import { useAuthContext } from '../AuthContext';
import { gql } from '@apollo/client';
import {
  useEndShiftForEmployeeMutation,
  useGetCurrentShiftForMechanicQuery,
  useStartShiftForEmployeeMutation,
} from './__generated__/ManageShiftComponent.generated';
import { Clock, RotateCw } from 'lucide-react';
import { useShiftDataUpdater } from './useShiftDataUpdater';
import { useLoginAsShop } from '../Pages/DashboardPage/useLoginAsShopFromEmployee';

export interface IManageShiftComponentProps {
  onClose: () => void;
}

export const ManageShiftComponent: React.FunctionComponent<
  IManageShiftComponentProps
> = props => {
  const { shiftData, isGettingShift, refetchShift } = useGetCurrentShift();
  const { loginAsShop, isLoggingAsShop } = useLoginAsShop();
  const { startShift, isStartingShift } = useStartShift();
  const { isEndingShift, endShift } = useEndShift();
  const authContext = useAuthContext();
  const employeeName = [
    authContext.user?.employee?.firstName,
    authContext.user?.employee?.lastName,
  ].join(' ');
  const title = employeeName ? `Welcome, ${employeeName}!` : 'Welcome!';
  return (
    <Dialog
      open={true}
      onOpenChange={newOpen => {
        if (!newOpen) {
          loginAsShop(() => {
            props.onClose();
          });
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col items-center gap-5'>
          {isLoggingAsShop && (
            <RotateCw className='ml-2 h-4 w-4 animate-spin' />
          )}
          {shiftData && (
            <div className='flex flex-col items-center'>
              <div className='font-semibold text-gray-600'>Shift</div>
              <div className='text-2xl font-bold text-gray-600'>
                {shiftData.hours} hours {shiftData.minutes} min
              </div>
              <div className='flex items-center text-sm'>
                <Clock className='mr-2 h-4 w-4' />
                <span>Started at {shiftData.startTime}</span>
              </div>
            </div>
          )}
          {!isGettingShift && !shiftData && (
            <Button
              variant={'outline'}
              onClick={() => {
                startShift(() => {
                  refetchShift();
                });
              }}
              disabled={isLoggingAsShop || isStartingShift || isEndingShift}
            >
              <span>Start your shift</span>
              {isStartingShift && (
                <RotateCw className='ml-2 h-4 w-4 animate-spin' />
              )}
            </Button>
          )}
          {!isGettingShift && shiftData && (
            <Button
              variant={'outline'}
              onClick={() => {
                endShift(() => {
                  loginAsShop(() => {
                    props.onClose();
                  });
                });
              }}
              disabled={isLoggingAsShop || isStartingShift || isEndingShift}
            >
              <span>End your shift</span>
              {isEndingShift && (
                <RotateCw className='ml-2 h-4 w-4 animate-spin' />
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function useGetCurrentShift() {
  const toast = useHotToast();
  const { data, loading, refetch } = useGetCurrentShiftForMechanicQuery({
    fetchPolicy: 'network-only',
    onError: error => {
      toast.error(error.message);
    },
  });
  const shiftData = useShiftDataUpdater(data?.getCurrentShift);
  return {
    shiftData,
    isGettingShift: loading,
    refetchShift: refetch,
  };
}

const query2 = gql`
  query GetCurrentShiftForMechanic {
    getCurrentShift {
      id
      startTime
      endTime
    }
  }
`;

function useStartShift() {
  const toast = useHotToast();
  const [startShiftMutation, { loading: isStartingShift }] =
    useStartShiftForEmployeeMutation({
      onError: e => {
        toast.error(e.message);
      },
    });
  return {
    isStartingShift,
    startShift: (onShiftStarted: () => void) => {
      startShiftMutation({
        onCompleted: onShiftStarted,
      });
    },
  };
}

const query = gql`
  mutation StartShiftForEmployee {
    employeeStartShift {
      id
      startTime
    }
  }
`;

function useEndShift() {
  const toast = useHotToast();
  const [endShiftMutation, { loading: isEndingShift }] =
    useEndShiftForEmployeeMutation({
      onError: e => {
        toast.error(e.message);
      },
    });
  return {
    isEndingShift,
    endShift: (onShiftEnded: () => void) => {
      endShiftMutation({
        onCompleted: onShiftEnded,
      });
    },
  };
}

const mutation = gql`
  mutation EndShiftForEmployee {
    employeeEndShift {
      id
      startTime
    }
  }
`;
